import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Check, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useCategories, useCatLabel, useErrorToast } from "@/lib/hooks";
import { useWs } from "@/context/Auth";
import { useI18n } from "@/i18n/I18n";

const Row = ({ cat, label, canWrite, onDone }) => {
  const { t } = useI18n();
  const showErr = useErrorToast();
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(label);
  const save = async () => { try { await api.put(`/categories/${cat.id}`, { name }); setEdit(false); onDone(); } catch (e) { showErr(e); } };
  const del = async () => { if (window.confirm(t("common.confirmDelete"))) { await api.delete(`/categories/${cat.id}`); onDone(); } };
  return (
    <li className="flex items-center gap-2 px-4 py-2.5" data-testid={`category-row-${cat.id}`}>
      {edit ? <Input data-testid={`category-edit-input-${cat.id}`} className="h-10" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        : <span className="flex-1 font-medium">{label}</span>}
      {canWrite && (edit
        ? <Button size="icon" variant="ghost" onClick={save} data-testid={`category-save-${cat.id}`}><Check className="w-4 h-4" /></Button>
        : <>
          <Button size="icon" variant="ghost" onClick={() => { setName(label); setEdit(true); }} data-testid={`category-edit-${cat.id}`}><Pencil className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" className="text-red-600" onClick={del} data-testid={`category-delete-${cat.id}`}><Trash2 className="w-4 h-4" /></Button>
        </>)}
    </li>
  );
};

export default function Categories() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { canWrite } = useWs();
  const showErr = useErrorToast();
  const { data: cats = [] } = useCategories();
  const catLabel = useCatLabel();
  const [name, setName] = useState("");
  const done = () => qc.invalidateQueries({ queryKey: ["categories"] });
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/categories", { name }); setName(""); done(); } catch (err) { showErr(err); }
  };
  return (
    <div className="max-w-2xl">
      <PageHeader title={t("cat.title")} />
      {canWrite && (
        <form onSubmit={add} className="flex gap-2 mb-6">
          <Input data-testid="category-name-input" className="h-12" placeholder={t("cat.add")} value={name} onChange={(e) => setName(e.target.value)} required />
          <Button data-testid="category-add-button" type="submit" className="h-12 gap-2"><Plus className="w-4 h-4" />{t("common.create")}</Button>
        </form>
      )}
      <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white" data-testid="category-list">
        {cats.map((c) => <Row key={c.id} cat={c} label={catLabel(c)} canWrite={canWrite} onDone={done} />)}
      </ul>
    </div>
  );
}
